import {
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  memo,
} from 'react'
import styled from '~/app/components/styled'
import { keyframes } from '@emotion/react'
import { Mixins } from '@yanolja-org/ya-com-core'
import useIsomorphicLayoutEffect from '~/app/utils/useIsomorphicLayoutEffect'
import { isSafari, isIOS, osVersion, browserVersion } from 'react-device-detect'

const expandKeyframe = (from: number, to: number) => keyframes`
  0% {
    height: ${from}px;
  }
  99% {
    height: ${to}px;
  }
  100%{
    height: auto;
  }
`

const collapseKeyframe = (from: number, to: number) => keyframes`
  0% {
    height: ${from}px;
  }
  100% {
    height: ${to}px;
  }
`
const Container = styled.div<{ min: number; max: number }>`
  transform: translate3d(0, 0, 0);
  overflow: hidden;
  min-height: 0;

  &.isShowing {
    animation: ${({ min, max }) => expandKeyframe(min, max)} 0.3s forwards;
  }

  &.isShowFinished {
    height: auto;
  }

  &.isCollapsing {
    animation: ${({ min, max }) => collapseKeyframe(max, min)} 0.3s forwards;
  }

  &.isCollapseFinished {
    height: ${({ min }) => min}px;
  }
`

const Content = styled.div<{ isShow: boolean; lineClamp?: number; isLineClampDisable: boolean }>`
  ${({ isShow, lineClamp, isLineClampDisable }) =>
    !lineClamp || isShow || isLineClampDisable ? '' : Mixins.LineClamp(lineClamp ?? 6)};
`

const Hidden = styled.div`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  visibility: hidden;
`

/**
 *  @summary  자세히 보기 따위의 버튼을 클릭하였을 때 children 을 보여주는 컴포넌트
 *  @param {boolean} isShow children 노출 여부
 *  @param {ReactNode} children 노출될 children
 *  @param {number} minHeight isShow 가 false 일때 노출되어야 하는 children 의 최소 높이
 *  @param {function} setShowMoreButton 자세히 보기 버튼 노출 state 의 setter 함수.
 *  @param {object} collapseOption 접힘 옵션
 *  @param {number} collapseOption.lineClamp 접힘 상태에서 노출되는 텍스트 라인 수
 * */
const CollapseContainer = ({
  isShow,
  children,
  minHeight = 0,
  setShowMoreButton,
  collapseOption,
}: PropsWithChildren<{
  isShow: boolean
  minHeight?: number
  setShowMoreButton?: Dispatch<SetStateAction<boolean>>
  collapseOption?: {
    lineClamp?: number
  }
}>) => {
  const originRef = useRef<HTMLDivElement>(null)
  // 원본 children 의 높이
  const [originHeight, setOriginHeight] = useState(minHeight)

  // reflow 를 발생시키지 않는 intersectionObserver 로 content 의 높이를 측정한다.
  useEffect(() => {
    const handleResize = () => {
      const io = new IntersectionObserver(entries =>
        setOriginHeight(entries[0].boundingClientRect.height)
      )

      if (originRef.current) {
        io.observe(originRef.current)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [children])

  // content 의 높이가 minHeight 보다 같거나 작을 경우에는 더보기 버튼을 노출할 필요가 없음
  useEffect(() => {
    // originHeight 의 소수점이 있는 경우가 있어 소수점 제거
    if (setShowMoreButton) setShowMoreButton(Math.floor(originHeight) > minHeight)
  }, [setShowMoreButton, originHeight, minHeight])

  /**
   * 최초 렌더링시에는 cls 를 최소화 하기 위해 maxHeight 을 minHeight 로 설정한다.
   * 이유는 초기 높이가 maxHeight -> minHeight 로 변동되는 과정에서 cls가 발생함.
   * 이후 토글 버튼 클릭이 발생했을때 maxHeight 를 contentHeight 로 설정한다.
   */
  const [clickCount, setClickCount] = useState(() => {
    // isShow 초기값이 true 일 경우에는 이미 한번 클릭한 것으로 간주한다.
    if (isShow) return 1
    return 0
  })

  useEffect(() => {
    setClickCount(p => p + 1)
  }, [isShow])

  const [isAnimationEnable, setIsAnimationEnable] = useState(false)

  // 브라우저가 paint 를 하기 전에 isAnimationEnable 을 true 로 설정하여 애니메이션을 발생시킨다.
  // 이후 onAnimationEnd 이벤트를 통해 isAnimationEnable 을 false 로 설정한다.
  // 이는 클릭이 발생한 경우가 아닌 이외의 경우 (예: 컴포넌트 재랜더) 에는 애니메이션 발생을 방지하기 위함이다.
  useIsomorphicLayoutEffect(() => {
    setIsAnimationEnable(true)
  }, [isShow])

  const containerClassName = useMemo(() => {
    if (isAnimationEnable) {
      return isShow ? 'isShowing' : 'isCollapsing'
    }

    return isShow ? 'isShowFinished' : 'isCollapseFinished'
  }, [isShow, isAnimationEnable])

  const maxHeight = useMemo(() => {
    if (isShow) {
      return originHeight
    }
    const isButtonClicked = clickCount > 1
    return isButtonClicked ? originHeight : minHeight
  }, [isShow, originHeight, clickCount, minHeight])

  // originHeight 가 minHeight 보다 작을 경우에는 불필요한 공간을 차지하지 못하게 minHeight 를 originHeight 로 설정한다.
  const minHeightComparedWithOriginHeight = useMemo(() => {
    if (!isShow) {
      return originHeight < minHeight ? originHeight : minHeight
    }
    return minHeight
  }, [minHeight, originHeight, isShow, clickCount])

  // Safari version 17 미만에서 inline element 만 lineClamp 가 적용되던 이슈 수정
  // https://developer.apple.com/documentation/safari-release-notes/safari-17-release-notes
  const [isLineClampDisable, setIsLineClampDisable] = useState(false)

  // https://github.com/duskload/react-device-detect/issues/196
  // SSR 이후 hydration 과정에서 react-device-detect 제공 변수들이 정상적으로 업데이트가 되지 않는 이슈가 있음.
  // 그래서 useMemo 가 아닌 state 를 사용하여 useEffect 로 업데이트 함
  useEffect(() => {
    const safariLineClampDisable = isSafari && +browserVersion < 17

    const [osMajorVersion] = osVersion.split('.').map(v => +v)
    const appleWebkitLineClampDisable = isIOS && +osMajorVersion < 17

    setIsLineClampDisable(safariLineClampDisable || appleWebkitLineClampDisable)
  }, [isSafari, isIOS, osVersion, browserVersion])

  return (
    <Container
      className={containerClassName}
      min={minHeightComparedWithOriginHeight}
      max={maxHeight}
      onAnimationEnd={() => setIsAnimationEnable(false)}
    >
      <Hidden ref={originRef}>{children}</Hidden>
      <Content
        lineClamp={collapseOption?.lineClamp}
        isShow={isShow}
        isLineClampDisable={isLineClampDisable}
      >
        {children}
      </Content>
    </Container>
  )
}

export default memo(CollapseContainer)
